"use client"

import { FC, useState } from "react"
import { errorText } from "src/components/common/errorText"
import { PrimaryButton } from "src/components/common/PrimaryButton"
import { ChevronDownIcon } from "src/components/icons"
import { MobileActionsModal } from "src/components/product/components/MobileActionsModal"
import { ProductPrice } from "src/components/product/components/ProductPrice"
import { useEventCallback } from "src/hooks/useEventCallback"
import { localizeText } from "src/localization/utils"
import { StorefrontDetailProduct, StorefrontDetailVariant } from "src/types/storefront"

export type MobileActionsProps = {
  product: StorefrontDetailProduct
  selectedVariant: StorefrontDetailVariant | undefined
  selections: Record<string, string>
  onValueClick: (optionId: string, valueName: string) => void
  outOfStock?: boolean
  comingSoon?: boolean
  onAddToCart: () => void
  disabled: boolean
  pending: boolean
}

export const MobileActions: FC<MobileActionsProps> = ({
  product,
  selectedVariant,
  selections,
  onValueClick,
  outOfStock,
  comingSoon,
  onAddToCart,
  disabled,
  pending,
}) => {
  const T = localizeText(productActionsText)

  const [isOpen, setOpen] = useState(false)
  const open = useEventCallback(() => setOpen(true))
  const close = useEventCallback(() => setOpen(false))

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-10 sm:hidden">
        <div className="flex size-full flex-col items-end justify-center gap-3 border-t bg-white p-4 text-base font-normal">
          <div className="flex w-full flex-col items-end gap-1">
            <ProductPrice product={product} variant={selectedVariant} />
          </div>

          <div className="flex w-full justify-center gap-4">
            {!!product.options?.length && (
              <PrimaryButton size="small" color="tertiary" padding="small" onClick={open}>
                <div className="flex w-full items-center justify-between">
                  <span>{selectedVariant ? T.changeVariant : T.selectVariant}</span>
                  <ChevronDownIcon />
                </div>
              </PrimaryButton>
            )}

            <PrimaryButton
              size="small"
              color="primary"
              padding="small"
              loading={pending}
              onClick={onAddToCart}
              disabled={comingSoon || outOfStock || !selectedVariant || disabled}
            >
              {comingSoon ? T.comingSoon : outOfStock ? T.outOfStock : T.addToCart}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {isOpen && (
        <MobileActionsModal
          product={product}
          selections={selections}
          onValueClick={onValueClick}
          optionsDisabled={pending}
          onClose={close}
        />
      )}
    </>
  )
}

const de = {
  selectVariant: "Variante wählen",
  changeVariant: "Variante ändern",
  outOfStock: "Ausverkauft",
  comingSoon: "Bald verfügbar",
  addToCart: "In den Warenkorb",
  errors: {
    fallback: errorText.de.message,
    insufficient_inventory: "Nicht genügend Lagerbestand für diese Menge.",
  },
}

const en: typeof de = {
  selectVariant: "Select variant",
  changeVariant: "Change variant",
  outOfStock: "Out of stock",
  comingSoon: "Coming soon",
  addToCart: "Add to cart",
  errors: {
    fallback: errorText.en.message,
    insufficient_inventory: "Insufficient inventory for this quantity.",
  },
}

export const productActionsText = { en, de }
